import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';
import { AdapterContext, generateIdentity, NamedAdapter, setIdentity } from 'shell-models-lib';
import { stringifyParameterValue } from 'app/shared/utils/parameter-converter.util';
import { Variable } from '../base/variable';
import { getString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { IPipelineParameterVariableContext } from 'app/interfaces/copilot.pipeline.action.types';
import { ParameterType } from 'app/shared/constants/parameter-constants';
import { ParameterAdapter } from './parameter.adapter';

type supportedVariableTypes = string | boolean | string[] | number;
@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Variable)
})
export class VariableAdapter extends NamedAdapter<
    Variable,
    Model.VariableSpecification,
    IPipelineParameterVariableContext
> {
    public static getTypeFromFabricContext = ParameterAdapter.getParameterTypeFromFabricContext;
    public static convertTypeToFabricContext = ParameterAdapter.convertParameterTypeToFabricContext;

    public override toFabricCopilotContext(variable: Variable): IPipelineParameterVariableContext {
        if (variable) {
            const variableResource = this.toResource(variable);
            const result: IPipelineParameterVariableContext = {
                Name: getString(variable.name),
                Type: VariableAdapter.convertTypeToFabricContext(variableResource.type as ParameterType),
                DefaultValue: variableResource.defaultValue
            };
            return result;
        }
        return undefined;
    }

    public override fromFabricCopilotContext(context: IPipelineParameterVariableContext, model?: Variable): Variable {
        if (context) {
            model = this._toModel(
                {
                    type: ParameterAdapter.getParameterTypeFromFabricContext(context.Type),
                    defaultValue: context.DefaultValue ?? undefined
                },
                model,
                context.Name
            );
        }
        return model;
    }

    protected _toResource(variable: Variable): Model.VariableSpecification {
        const variableSpecification: Model.VariableSpecification = {
            type: variable._type
        };
        if (variable.defaultValue) {
            variableSpecification.defaultValue = this.convertVariableValue(variable.defaultValue, variable._type);
        }

        if (this.context.writeIdentities) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (variableSpecification as any).identity = this.context.identityProvider.identity(variable);
        }
        return variableSpecification;
    }

    protected _toModel(
        modelVariable: Model.VariableSpecification,
        variable?: Variable,
        variableName?: string,
        ownerType?: string,
        ownerName?: string
    ): Variable {
        if (!variable) {
            variable = new Variable(variableName);
        }
        // eslint-disable-next-line
        if (modelVariable.defaultValue !== undefined && modelVariable.defaultValue !== null) {
            variable.defaultValue = stringifyParameterValue(modelVariable.defaultValue);
        }
        variable._type = modelVariable.type;

        if (this.context.adapterConfig.replaceIdentitiesForNonConcepts) {
            // If we are getting these assertion we have to fix the calling code.
            const parameterIdentity = AdapterContext.getIdentity(
                generateIdentity(ownerType, ownerName),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                variable.name!
            );

            setIdentity(variable, parameterIdentity);
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const resource = modelVariable as any;
            if (resource.identity) {
                setIdentity(variable, resource.identity);
            }
        }

        return variable;
    }

    protected convertVariableValue(value: string, valueType: string): supportedVariableTypes {
        if (stringIsNullOrEmpty(value)) {
            return undefined;
        }
        if (!valueType) {
            this._logWarning(`Missing param type, for parameter value - ${value}`);
            return value;
        }
        switch (valueType.toLowerCase()) {
            case 'string':
                break;
            case 'boolean':
                if (String(value) === 'true' || String(value) === 'false') {
                    return value === 'true';
                }
                break;
            case 'array':
                try {
                    const object = JSON.parse(value);
                    if (typeof object === 'object') {
                        return object;
                    }
                } catch {
                    // If type is array and the value is not in array form i.e [] is missing around the value, we will turn it into an array
                    // 1,2,"3" --> [1,2,"3"]   1,2," -> [1,2,"\""]
                    return value.split(/,(?![^\(\[]*[\]\)])/).map((val: string) => {
                        if (val.startsWith('"') && val.endsWith('"')) {
                            return val.substring(1, val.length - 1);
                        }
                        return val;
                    });
                }
                break;
            case 'integer':
                if (Number.isInteger(Number(value))) {
                    return Number(value);
                }
                break;
            default:
                this._logWarning(`Unhandled param type: ${valueType.toLowerCase()} , for parameter value - ${value}`);
                break;
        }
        return value;
    }
}
